import React, { Component } from 'react';
import { Button, Icon, Intent } from '@blueprintjs/core';
import { observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Helmet } from 'react-helmet';
import Toast from 'helpers/toaster';
import { FormSection, FieldError } from 'components/FormBits';
import SettingsPage, { SettingsCard } from 'components/SettingsPage';
import AccountSidebar from 'components/AccountSidebar';

export const MIN_LENGTH = 8;

// Field -> input id, in the order they appear (the first with a problem gets focus).
const FIELD_IDS = {
  current_password: 'password-current',
  new_password: 'password-new',
  new_password_confirmation: 'password-confirm',
};

// Change your own password (/dashboard/users/change-password, from the account
// menu). Saving signs you out (viewStore.performPasswordChange), so the page
// says so up front.
@inject('viewStore')
@observer
class ChangePassword extends Component {
  @observable user = {
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  };
  @observable errors = {};
  @observable visible = {}; // field -> shown as plain text

  setField(key, value) {
    this.user[key] = value;
    if (this.errors[key]) this.errors = { ...this.errors, [key]: null };
  }

  validate() {
    const { current_password, new_password, new_password_confirmation } = this.user;
    const errors = {};
    if (!current_password) errors.current_password = 'Enter your current password.';
    if (!new_password) errors.new_password = 'Enter a new password.';
    else if (new_password.length < MIN_LENGTH) errors.new_password = `Use at least ${MIN_LENGTH} characters.`;
    if (new_password && new_password_confirmation !== new_password) {
      errors.new_password_confirmation = 'Type the same new password again.';
    }
    return errors;
  }

  submit = (event) => {
    if (event) event.preventDefault();
    const { viewStore } = this.props;
    if (viewStore.performingChangePassword) return;
    const errors = this.validate();
    this.errors = errors;
    const first = Object.keys(FIELD_IDS).find((key) => errors[key]);
    if (first) {
      const field = document.getElementById(FIELD_IDS[first]);
      if (field) field.focus();
      return;
    }
    viewStore.performPasswordChange(this.user, viewStore.currentUser.id)
      .then(() => {
        Toast.show({ message: 'Password changed. Sign in with your new password.', intent: Intent.SUCCESS });
      })
      .catch((error) => {
        const data = error && error.response && error.response.data;
        const reason = data && (data.errors || data.error);
        Toast.show({
          message: reason ? (Array.isArray(reason) ? reason.join(', ') : reason) : "Couldn't change your password. Try again.",
          intent: Intent.DANGER,
        });
      });
  };

  passwordField(key, label, autoComplete, describedBy) {
    const id = FIELD_IDS[key];
    const error = this.errors[key];
    const shown = Boolean(this.visible[key]);
    const describers = [error && `${id}-error`, describedBy].filter(Boolean).join(' ') || undefined;
    return (
      <div className={`bp3-form-group${error ? ' has-error' : ''}`}>
        <label className="fb-label" htmlFor={id}>
          {label}
          <span className="fb-required" aria-hidden="true"> *</span>
        </label>
        <div className="pw-field">
          <input
            id={id}
            name={key}
            type={shown ? 'text' : 'password'}
            className="bp3-input bp3-fill"
            autoComplete={autoComplete}
            spellCheck={false}
            aria-required="true"
            aria-invalid={error ? true : undefined}
            aria-describedby={describers}
            value={this.user[key]}
            onChange={(event) => this.setField(key, event.target.value)}
          />
          <button
            type="button"
            className="pw-field__toggle"
            aria-controls={id}
            aria-pressed={shown}
            onClick={() => { this.visible = { ...this.visible, [key]: !shown }; }}
          >
            {shown ? 'Hide' : 'Show'}
          </button>
        </div>
        <FieldError id={`${id}-error`}>{error}</FieldError>
      </div>
    );
  }

  // What the new password needs, ticked off as it's typed.
  renderChecks() {
    const { new_password, new_password_confirmation } = this.user;
    const checks = [
      { key: 'length', met: new_password.length >= MIN_LENGTH, label: `At least ${MIN_LENGTH} characters` },
      { key: 'match', met: Boolean(new_password) && new_password === new_password_confirmation, label: 'Both new passwords match' },
    ];
    return (
      <ul className="pw-checks" id="password-checks" aria-label="New password needs">
        {checks.map((check) => (
          <li key={check.key} className={`pw-check${check.met ? ' is-met' : ''}`}>
            <Icon icon={check.met ? 'tick-circle' : 'circle'} iconSize={14} aria-hidden="true" />
            <span>{check.label}</span>
            <span className="sr-only">{check.met ? ' (done)' : ' (not yet)'}</span>
          </li>
        ))}
      </ul>
    );
  }

  render() {
    const { viewStore } = this.props;
    const email = viewStore.currentUser && viewStore.currentUser.attributes && viewStore.currentUser.attributes.email;
    return (
      <React.Fragment>
        <Helmet>
          <title>Change password | FieldNexus</title>
        </Helmet>
        <SettingsPage
          sidebar={<AccountSidebar />}
          title="Change password"
          description="After it's changed you'll be signed out. Sign in again with the new password."
        >
          <form className="pw-form" onSubmit={this.submit} noValidate>
            {/* Lets password managers save the new password against this account. */}
            {email && <input type="email" name="username" autoComplete="username" value={email} readOnly hidden />}
            <SettingsCard
              className="pw-card"
              footer={
                <Button
                  type="submit"
                  className="sp-button sp-button--primary"
                  icon="key"
                  text="Change password"
                  loading={viewStore.performingChangePassword}
                />
              }
            >
              <FormSection title="Your password" hint="Your current one confirms it's you. Pick a new one you don't use anywhere else.">
                {this.passwordField('current_password', 'Current password', 'current-password')}
                {this.passwordField('new_password', 'New password', 'new-password', 'password-checks')}
                {this.passwordField('new_password_confirmation', 'New password again', 'new-password', 'password-checks')}
                {this.renderChecks()}
              </FormSection>
            </SettingsCard>
          </form>
        </SettingsPage>
      </React.Fragment>
    );
  }
}

export default ChangePassword;
